import DOMPurify from "dompurify";
import { Marked } from "marked";
import { useMemo, useRef, useState } from "react";

import { cn } from "../../../lib/utils";
import { wrapMarkdownTables } from "../../jan/utils/wrapMarkdownTables";
import { JanIcon } from "../../jan/components/atoms/JanIcon";
import { MermaidDiagram } from "../../jan/components/atoms/MermaidDiagram";
import { UserInitialIcon } from "../../jan/components/atoms/UserInitialIcon";
import type { PublicChatMessage } from "../api/publicChat";
import {
  handleCopy,
  handleDownloadDocx,
  handleDownloadPdf,
} from "../utils/publicActions";

interface Props {
  message: PublicChatMessage;
  isStreaming?: boolean;
  showActions?: boolean;
}

// Local marked instance with link renderer that opens in new tab.
const md = new Marked({ gfm: true, breaks: true });
md.use({
  renderer: {
    link({ href, title, text }) {
      const titleAttr = title ? ` title="${title}"` : "";
      return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
    },
  },
});

const renderMarkdown = (input: string): string => {
  const raw = md.parse(input, { async: false });
  const html = typeof raw === "string" ? raw : "";
  return wrapMarkdownTables(
    DOMPurify.sanitize(html, { ADD_ATTR: ["target", "rel"] })
  );
};

type ContentSegment =
  | { type: "html"; html: string }
  | { type: "mermaid"; source: string };

const MERMAID_PLACEHOLDER = "___MERMAID_BLOCK_";

// Extract mermaid blocks before markdown parsing so they don't render as
// raw <pre><code class="language-mermaid">. Mirrors the clinician bubble
// at frontend/src/pages/jan/components/atoms/ChatMessageBubble.
const parseSegments = (content: string): ContentSegment[] => {
  if (!content) return [];

  const mermaidBlocks: string[] = [];
  const withPlaceholders = content.replace(
    /```mermaid\n([\s\S]*?)```/g,
    (_match, code: string) => {
      const index = mermaidBlocks.length;
      mermaidBlocks.push(code.trim());
      return `${MERMAID_PLACEHOLDER}${index}___`;
    }
  );

  if (mermaidBlocks.length === 0) {
    return [{ type: "html", html: renderMarkdown(content) }];
  }

  const parts = withPlaceholders.split(
    new RegExp(`(${MERMAID_PLACEHOLDER}\\d+___)`)
  );

  const result: ContentSegment[] = [];
  for (const part of parts) {
    const placeholderMatch = part.match(
      new RegExp(`^${MERMAID_PLACEHOLDER}(\\d+)___$`)
    );
    if (placeholderMatch) {
      const index = parseInt(placeholderMatch[1], 10);
      result.push({ type: "mermaid", source: mermaidBlocks[index] });
    } else if (part.trim()) {
      result.push({ type: "html", html: renderMarkdown(part) });
    }
  }
  return result;
};

export const PublicMessageBubble = ({
  message,
  isStreaming,
  showActions = true,
}: Props) => {
  const [busy, setBusy] = useState<"copy" | "pdf" | "docx" | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isUser = message.role === "user";

  const segments = useMemo(
    () => (!isUser ? parseSegments(message.content) : []),
    [isUser, message.content]
  );

  const onAction = async (kind: "copy" | "pdf" | "docx") => {
    if (busy) return;
    setBusy(kind);
    try {
      if (kind === "copy") await handleCopy(message.content);
      else if (kind === "pdf")
        await handleDownloadPdf(message.content, contentRef.current);
      else await handleDownloadDocx(message.content, contentRef.current);
    } finally {
      setBusy(null);
    }
  };

  const hasTable = segments.some(
    (segment) =>
      segment.type === "html" && segment.html.includes('class="jan-table"')
  );

  const proseClasses = cn(
    "prose prose-sm max-w-none break-words text-sm leading-relaxed select-text",
    "prose-headings:mt-2 prose-headings:mb-2 prose-headings:font-semibold",
    "prose-p:my-1 prose-p:first:mt-0 prose-p:last:mb-0",
    "prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5",
    "prose-code:text-xs prose-code:px-1 prose-code:py-0.5 prose-code:rounded",
    "prose-pre:my-1 prose-pre:text-xs",
    "prose-a:underline prose-a:underline-offset-2",
    isUser
      ? "prose-invert prose-headings:text-white prose-p:text-white prose-strong:text-white prose-em:text-white prose-code:bg-slate-600 prose-code:text-white prose-pre:bg-slate-600 prose-pre:text-white prose-li:text-white prose-a:text-blue-300"
      : "prose-slate prose-headings:text-slate-800 prose-p:text-slate-800 prose-strong:text-slate-900 prose-em:text-slate-700 prose-code:bg-slate-200 prose-code:text-slate-900 prose-pre:bg-slate-200 prose-pre:text-slate-800 prose-li:text-slate-800 prose-a:text-blue-600"
  );

  return (
    <div
      className={cn(
        // Avatar is absolutely positioned (not a flex sibling) so the message
        // text stays in normal block flow. Chromium's flexbox text-selection
        // bug otherwise lets a drag-selection extend only top->bottom, so users
        // could copy just part of a message (nov-1300).
        "relative py-4",
        isUser ? "pr-11 text-right" : "pl-11 text-left"
      )}
    >
      <div className={cn("absolute top-5", isUser ? "right-0" : "left-0")}>
        {isUser ? (
          <UserInitialIcon name="You" size="md" />
        ) : (
          <JanIcon size="md" />
        )}
      </div>

      <div className="min-w-0">
        <div
          className={cn(
            hasTable
              ? "block w-full max-w-[92%] min-w-0 px-4 py-3 rounded-2xl text-left"
              : "inline-block max-w-[92%] px-4 py-3 rounded-2xl text-left",
            isUser
              ? "bg-slate-700 text-white rounded-br-md"
              : "bg-slate-100 text-slate-800 rounded-bl-md"
          )}
        >
          <div ref={contentRef} className={proseClasses}>
            {isUser ? (
              <p className="whitespace-pre-wrap">{message.content}</p>
            ) : (
              segments.map((segment, i) =>
                segment.type === "mermaid" && !isStreaming ? (
                  <MermaidDiagram key={i} source={segment.source} />
                ) : segment.type === "mermaid" ? (
                  // While streaming, the mermaid block may still be partial —
                  // show it as code until the message completes.
                  <pre key={i} className="my-1 text-xs">
                    <code>{segment.source}</code>
                  </pre>
                ) : (
                  <div
                    key={i}
                    dangerouslySetInnerHTML={{ __html: segment.html }}
                  />
                )
              )
            )}
          </div>
          {isStreaming ? (
            <span
              data-testid="streaming-cursor"
              className="inline-block w-1.5 h-4 ml-0.5 bg-current animate-pulse"
            />
          ) : null}
        </div>

        {showActions && !isUser && message.content && !isStreaming ? (
          <div className="mt-1.5 flex gap-3 text-xs text-slate-400">
            <button
              type="button"
              onClick={() => onAction("copy")}
              disabled={busy !== null}
              className="hover:text-slate-600 disabled:opacity-50 transition-colors"
            >
              {busy === "copy" ? "Copied!" : "Copy"}
            </button>
            <button
              type="button"
              onClick={() => onAction("pdf")}
              disabled={busy !== null}
              className="hover:text-slate-600 disabled:opacity-50 transition-colors"
            >
              PDF
            </button>
            <button
              type="button"
              onClick={() => onAction("docx")}
              disabled={busy !== null}
              className="hover:text-slate-600 disabled:opacity-50 transition-colors"
            >
              DOCX
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};
